import moment from "moment";
import { ApiUserMinimal } from "src/api/types";
import Avatar from "src/components/Avatar";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import useTeams from "src/hooks/api/services/users/useTeams";
import useUsers from "src/hooks/api/services/users/useUsers";
import useTaskForm from "src/hooks/api/tasks/useTaskForm";
import useLanguage from "src/hooks/api/useLanguage";
import useAuth from "src/hooks/selectors/useAuth";
import { filterByUnique } from "src/lib/filters";

interface TaskModalContentProps extends Translation {}

const TaskModalContent = ({ t }: TaskModalContentProps) => {
	const { language } = useLanguage();
	const { task } = useTaskForm();
	const { users } = useUsers();
	const auth = useAuth();
	const creator = task?.id
		? users.find((user) => user.id === task?.links?.creator_id)
		: auth;

	const { teams } = useTeams();
	const teamSlugs = (task?.assignees || [])
		.map((user: ApiUserMinimal) => user.teams)
		.flat();

	const linkedTeams = teams
		.filter((team) => (teamSlugs || []).includes(team.slug))
		.map((team) => team.name)
		.filter(filterByUnique);
	const formatter = new Intl.ListFormat(
		(language?.current || "NL").toLowerCase(),
		{
			style: "long",
			type: "conjunction",
		}
	);

	return (
		<div className="flex-1 flex flex-col">
			<div className="flex-1"></div>

			<div className="p-6 flex flex-col gap-4">
				{!!linkedTeams?.length && (
					<div className="flex flex-col gap-1">
						<strong>{t("team")}</strong>
						<p>{formatter.format(linkedTeams)}</p>
					</div>
				)}
				{creator && (
					<div className="flex flex-col gap-1">
						<strong>{t("created-by")}</strong>

						<Avatar.User
							user={creator}
							avatar={{
								size: "xsmall",
							}}
							subtitle={<></>}
							variant="light"
						/>
					</div>
				)}
			</div>
			{task?.id && (
				<>
					<hr className="border-dark opacity-10" />
					<div className="p-6 flex flex-col gap-4">
						<div className="flex flex-col gap-1">
							<strong>{t("update")}</strong>
							<span className="opacity-90">
								{moment(task.updated_at).format(
									"DD-MM-YYYY HH:mm"
								)}
							</span>
						</div>
						<div className="flex flex-col gap-1">
							<strong>{t("created")}</strong>
							<span className="opacity-90">
								{moment(task.created_at).format(
									"DD-MM-YYYY HH:mm"
								)}
							</span>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

TaskModalContent.locale = {
	nl: {
		title: "",
		update: "Geupdate",
		created: "Aangemaakt",
		"created-by": "Aangemaakt door",
		"assigned-to": "Uitvoerder",
		team: "Team",
	},
	en: {
		title: "",
		update: "Updated",
		created: "Created",
		"created-by": "Created by",
		"assigned-to": "Assignee",
		team: "Team",
	},
};

export default withTranslation(TaskModalContent);
